import { useState, useEffect } from 'react';
import { createCallReport, updateCallReport, searchCallReports } from '../../services/callReportService';
import { searchAIAgents } from '../../services/aiAgentService';
import type { RetellAIAgent } from '../../types/aiAgent';
import type { RetellCallReport, RetellCallScore, RetellCallScoreCriteria } from '../../types/callReport';

type View = 'main' | 'category' | 'criterion';

interface CallReportModalProps {
  report?: RetellCallReport;
  onClose: () => void;
  onSuccess: () => void;
}

export function CallReportModal({ report, onClose, onSuccess }: CallReportModalProps) {
  const isEdit = !!report;

  // Agents dropdown
  const [agents, setAgents] = useState<RetellAIAgent[]>([]);

  useEffect(() => {
    searchAIAgents({ active: true }, { pageSize: 1000, orderBy: 'name', order: 'asc' })
      .then((ags) => {
        setAgents(ags);
        if (!report && ags.length === 1) setAgentId(ags[0].retellAgentId);
      })
      .catch(() => {});
  }, []);

  // Main form state
  const [agentId, setAgentId] = useState(report?.agentId || '');
  const [name, setName] = useState(report?.name || '');
  const [categories, setCategories] = useState<RetellCallScore[]>(
    report?.scoreCategories ? JSON.parse(JSON.stringify(report.scoreCategories)) : [],
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Navigation state
  const [view, setView] = useState<View>('main');
  const [editingCategoryIndex, setEditingCategoryIndex] = useState<number | null>(null);
  const [editingCriterionIndex, setEditingCriterionIndex] = useState<number | null>(null);

  // Category form state
  const [catTitle, setCatTitle] = useState('');
  const [catHighlighted, setCatHighlighted] = useState(false);
  const [catWeight, setCatWeight] = useState(0);
  const [catOrder, setCatOrder] = useState(0);
  const [catCriteria, setCatCriteria] = useState<RetellCallScoreCriteria[]>([]);

  // Criterion form state
  const [critKey, setCritKey] = useState('');
  const [critText, setCritText] = useState('');
  const [critType, setCritType] = useState('boolean');

  // ── Category helpers ──────────────────────────────────────────────────────

  function openCategoryForm(index: number | null) {
    if (index !== null) {
      const cat = categories[index];
      setCatTitle(cat.title);
      setCatHighlighted(cat.highlighted);
      setCatWeight(cat.weight);
      setCatOrder(cat.order);
      setCatCriteria(cat.criteria ? [...cat.criteria] : []);
    } else {
      setCatTitle('');
      setCatHighlighted(false);
      setCatWeight(0);
      setCatOrder(categories.length);
      setCatCriteria([]);
    }
    setEditingCategoryIndex(index);
    setView('category');
  }

  function saveCategory() {
    if (!catTitle.trim()) return;
    const cat: RetellCallScore = {
      title: catTitle.trim(),
      highlighted: catHighlighted,
      weight: catWeight,
      order: catOrder,
      criteria: catCriteria.length > 0 ? catCriteria : undefined,
    };
    const updated = [...categories];
    if (editingCategoryIndex !== null) {
      updated[editingCategoryIndex] = cat;
    } else {
      updated.push(cat);
    }
    setCategories(updated);
    setView('main');
  }

  function deleteCategory(index: number) {
    setCategories(categories.filter((_, i) => i !== index));
  }

  // ── Criterion helpers ─────────────────────────────────────────────────────

  function openCriterionForm(index: number | null) {
    if (index !== null) {
      const crit = catCriteria[index];
      setCritKey(crit.key);
      setCritText(crit.text);
      setCritType(crit.type);
    } else {
      setCritKey('');
      setCritText('');
      setCritType('boolean');
    }
    setEditingCriterionIndex(index);
    setView('criterion');
  }

  function saveCriterion() {
    if (!critKey.trim() || !critText.trim()) return;
    const crit: RetellCallScoreCriteria = {
      key: critKey.trim(),
      text: critText.trim(),
      type: critType,
    };
    const updated = [...catCriteria];
    if (editingCriterionIndex !== null) {
      updated[editingCriterionIndex] = crit;
    } else {
      updated.push(crit);
    }
    setCatCriteria(updated);
    setView('category');
  }

  function deleteCriterion(index: number) {
    setCatCriteria(catCriteria.filter((_, i) => i !== index));
  }

  // ── Save ──────────────────────────────────────────────────────────────────

  async function handleSave() {
    if (!agentId.trim()) return;
    setError('');
    setLoading(true);
    try {
      if (isEdit) {
        await updateCallReport(report!.id, {
          ...report!,
          agentId: agentId.trim(),
          name: name.trim() || null,
          scoreCategories: categories,
        });
      } else {
        // Check if a call report already exists for this agent
        const existing = await searchCallReports({ agentId: agentId.trim() }, { pageSize: 100 });
        if (existing.some((r) => r.agentId === agentId.trim())) {
          setError('A call report already exists for this agent.');
          setLoading(false);
          return;
        }
        await createCallReport({
          agentId: agentId.trim(),
          name: name.trim() || undefined,
          scoreCategories: categories,
        });
      }
      onSuccess();
    } catch {
      setError(isEdit ? 'Failed to update call report' : 'Failed to create call report');
    } finally {
      setLoading(false);
    }
  }

  // ── Shared styles ─────────────────────────────────────────────────────────

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  const btnOutline = 'px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors';
  const btnPrimary = 'px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';
  const btnSmall = 'text-xs px-2 py-1 rounded transition-colors';

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">

        {/* ── Main Form ─────────────────────────────────────────────── */}
        {view === 'main' && (
          <>
            <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
              <h2 className="text-lg font-bold text-gray-900 mb-4">
                {isEdit ? 'Edit Call Report' : 'Add Call Report'}
              </h2>

              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Agent *</label>
                  <select value={agentId} onChange={(e) => setAgentId(e.target.value)} required className={inputClass}>
                    <option value="">Select agent</option>
                    {agents.map((a) => (
                      <option key={a.retellAgentId} value={a.retellAgentId}>{a.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Name</label>
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)} className={inputClass} placeholder="Name" />
                </div>

                {/* Score Categories */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className={labelClass}>Score Categories</label>
                    <button type="button" onClick={() => openCategoryForm(null)} className={`${btnSmall} text-blue-600 hover:bg-blue-50`}>+ Add</button>
                  </div>
                  {categories.length === 0 ? (
                    <p className="text-sm text-gray-400">No categories yet</p>
                  ) : (
                    <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                      {categories.map((cat, i) => (
                        <div key={i} className="flex items-center justify-between px-3 py-2">
                          <span className="text-sm text-gray-700">{cat.title} ({cat.criteria?.length || 0} criteria)</span>
                          <div className="flex items-center gap-1">
                            <button type="button" onClick={() => openCategoryForm(i)} className={`${btnSmall} text-gray-500 hover:bg-gray-100`}>Edit</button>
                            <button type="button" onClick={() => deleteCategory(i)} className={`${btnSmall} text-red-500 hover:bg-red-50`}>Delete</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

              </div>

              {error && <p className="text-red-600 text-sm mt-3">{error}</p>}
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button type="button" onClick={onClose} className={btnOutline}>Cancel</button>
              <button type="button" onClick={handleSave} disabled={loading || !agentId.trim()} className={btnPrimary}>
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </>
        )}

        {/* ── Category Form ─────────────────────────────────────────── */}
        {view === 'category' && (
          <>
            <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
              <h2 className="text-lg font-bold text-gray-900 mb-4">
                {editingCategoryIndex !== null ? 'Edit Category' : 'Add Category'}
              </h2>

              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Title *</label>
                  <input type="text" value={catTitle} onChange={(e) => setCatTitle(e.target.value)} required className={inputClass} placeholder="Category title" />
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <label className={labelClass}>Weight</label>
                    <input type="number" value={catWeight} onChange={(e) => setCatWeight(Number(e.target.value))} className={inputClass} />
                  </div>
                  <div className="flex-1">
                    <label className={labelClass}>Order</label>
                    <input type="number" value={catOrder} onChange={(e) => setCatOrder(Number(e.target.value))} className={inputClass} />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={catHighlighted} onChange={(e) => setCatHighlighted(e.target.checked)} className="rounded border-gray-300" />
                  Highlighted
                </label>

                {/* Criteria */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className={labelClass}>Criteria</label>
                    <button type="button" onClick={() => openCriterionForm(null)} className={`${btnSmall} text-blue-600 hover:bg-blue-50`}>+ Add</button>
                  </div>
                  {catCriteria.length === 0 ? (
                    <p className="text-sm text-gray-400">No criteria yet</p>
                  ) : (
                    <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                      {catCriteria.map((crit, i) => (
                        <div key={i} className="flex items-center justify-between px-3 py-2">
                          <span className="text-sm text-gray-700">{crit.key}</span>
                          <div className="flex items-center gap-1">
                            <button type="button" onClick={() => openCriterionForm(i)} className={`${btnSmall} text-gray-500 hover:bg-gray-100`}>Edit</button>
                            <button type="button" onClick={() => deleteCriterion(i)} className={`${btnSmall} text-red-500 hover:bg-red-50`}>Delete</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button type="button" onClick={() => setView('main')} className={btnOutline}>Back</button>
              <button type="button" onClick={saveCategory} disabled={!catTitle.trim()} className={btnPrimary}>Done</button>
            </div>
          </>
        )}

        {/* ── Criterion Form ────────────────────────────────────────── */}
        {view === 'criterion' && (
          <>
            <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
              <h2 className="text-lg font-bold text-gray-900 mb-4">
                {editingCriterionIndex !== null ? 'Edit Criterion' : 'Add Criterion'}
              </h2>

              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Key *</label>
                  <input type="text" value={critKey} onChange={(e) => setCritKey(e.target.value)} required className={inputClass} placeholder="Criterion key" />
                </div>
                <div>
                  <label className={labelClass}>Text *</label>
                  <input type="text" value={critText} onChange={(e) => setCritText(e.target.value)} required className={inputClass} placeholder="Criterion text" />
                </div>
                <div>
                  <label className={labelClass}>Type</label>
                  <select value={critType} onChange={(e) => setCritType(e.target.value)} className={inputClass}>
                    <option value="text">text</option>
                    <option value="boolean">boolean</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button type="button" onClick={() => setView('category')} className={btnOutline}>Back</button>
              <button type="button" onClick={saveCriterion} disabled={!critKey.trim() || !critText.trim()} className={btnPrimary}>Done</button>
            </div>
          </>
        )}

      </div>
    </div>
  );
}
